import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

const Notification = ({
  openSnackbar,
  snackbarSeverity,
  snackbarMessage,
  setOpenSnackbar,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      setOpenSnackbar(false);
    }, 300);
  }, [setOpenSnackbar]);

  useEffect(() => {
    if (openSnackbar) {
      setIsVisible(true);
      setIsAnimating(true);

      // Auto hide after 3 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [openSnackbar, handleClose]);

  const getNotificationConfig = (severity) => {
    switch (severity) {
      case "success":
        return {
          icon: faCheckCircle,
          bgGradient: "linear-gradient(135deg, #7cc890 0%, #6ab77d 100%)",
          borderColor: "#7cc890",
          iconColor: "#ffffff",
          textColor: "#ffffff",
          shadowColor: "rgba(124, 200, 144, 0.3)",
        };
      case "error":
        return {
          icon: faExclamationCircle,
          bgGradient: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
          borderColor: "#dc3545",
          iconColor: "#ffffff",
          textColor: "#ffffff",
          shadowColor: "rgba(220, 53, 69, 0.3)",
        };
      case "warning":
        return {
          icon: faExclamationTriangle,
          bgGradient: "linear-gradient(135deg, #ffc107 0%, #e0a800 100%)",
          borderColor: "#ffc107",
          iconColor: "#ffffff",
          textColor: "#ffffff",
          shadowColor: "rgba(255, 193, 7, 0.3)",
        };
      case "info":
      default:
        return {
          icon: faInfoCircle,
          bgGradient: "linear-gradient(135deg, #053251 0%, #074a6b 100%)",
          borderColor: "#053251",
          iconColor: "#ffffff",
          textColor: "#ffffff",
          shadowColor: "rgba(5, 50, 81, 0.3)",
        };
    }
  };

  const config = getNotificationConfig(snackbarSeverity);

  if (!isVisible) return null;

  return (
    <>
      <style jsx>{`
        .notification-container {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10000;
          pointer-events: none;
        }

        .notification {
          background: ${config.bgGradient};
          border: 1px solid ${config.borderColor};
          border-radius: 16px;
          padding: 16px 20px;
          min-width: 300px;
          max-width: 500px;
          box-shadow: 0 8px 32px ${config.shadowColor}, 0 4px 16px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          display: flex;
          align-items: center;
          gap: 12px;
          pointer-events: auto;
          position: relative;
          overflow: hidden;
          animation: ${isAnimating ? 'slideInDown' : 'slideOutUp'} 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-100px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideOutUp {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(-100px) scale(0.95);
          }
        }

        .notification::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          animation: shimmer 2s ease-in-out infinite;
        }

        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        .notification-icon {
          font-size: 20px;
          color: ${config.iconColor};
          flex-shrink: 0;
          animation: iconPulse 2s ease-in-out infinite;
        }

        @keyframes iconPulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        .notification-content {
          flex: 1;
          color: ${config.textColor};
          font-family: "iran-sans", "MyPersianFont", sans-serif;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.4;
          text-align: right;
          direction: rtl;
        }

        .notification-close {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 8px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: ${config.textColor};
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .notification-close:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .notification-close:active {
          transform: scale(0.95);
        }

        .progress-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 0 0 16px 16px;
          animation: progressShrink 3s linear forwards;
        }

        @keyframes progressShrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .notification-container {
            top: 10px;
            left: 10px;
            right: 10px;
            transform: none;
          }

          .notification {
            min-width: auto;
            width: 100%;
            padding: 14px 16px;
          }

          .notification-content {
            font-size: 13px;
          }

          .notification-icon {
            font-size: 18px;
          }
        }

        @media (max-width: 480px) {
          .notification {
            padding: 12px 14px;
            border-radius: 12px;
          }

          .notification-content {
            font-size: 12px;
          }

          .notification-close {
            width: 24px;
            height: 24px;
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .notification {
            border-width: 2px;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .notification {
            animation: none;
          }

          .notification-icon {
            animation: none;
          }

          .notification::before {
            animation: none;
          }
        }
      `}</style>

      <div className="notification-container">
        <div className="notification">
          <FontAwesomeIcon icon={config.icon} className="notification-icon" />
          <div className="notification-content">{snackbarMessage}</div>
          <button
            className="notification-close"
            onClick={(e) => handleClose(e, 'buttonClick')}
            aria-label="بستن اعلان"
          >
            <FontAwesomeIcon icon={faTimes} size="sm" />
          </button>
          <div className="progress-bar"></div>
        </div>
      </div>
    </>
  );
};

export default Notification;